// Typed client for the Studio evals REST API (/v1/studio/evals/*): rubric
// scores on sampled production calls, aggregated per guide version.
//
// Every read is scoped to ONE rubric id (omit rubricId = the active rubric):
// criteria are defined per rubric, so e.g. companion_v2 and companion_v3
// averages are never blended.

import { apiRequest } from "./http";
import type { CallRecord } from "./analytics";

export interface RubricCriterion {
  key: string;
  description: string;
}

export interface CriterionAvg {
  key: string;
  avg: number | null;
  count: number;
}

export interface RubricDefinition {
  rubricId: string;
  activeRubricId: string;
  rubricIds: string[];
  criteria: RubricCriterion[];
}

export interface RubricCount {
  rubricId: string;
  count: number;
  active: boolean;
}

export interface VersionSummary {
  version: number | null;
  count: number;
  overallAvg: number | null;
  criteria: CriterionAvg[];
}

export interface EvalSummary {
  guideId: string;
  // The rubric every number here was judged under.
  rubricId: string;
  count: number;
  overallAvg: number | null;
  criteria: CriterionAvg[];
  versions: VersionSummary[];
  // Rubrics with scored calls in range; the active one is always listed.
  rubrics: RubricCount[];
}

export interface CriterionScore {
  key: string;
  score: number;
  // False when the conversation gave no opportunity to satisfy or violate
  // the criterion (the rubric's "mark not applicable" set). Absent on
  // companion_v1 rows — treat missing as applicable.
  applicable?: boolean;
  rationale: string;
}

export interface CallEvaluation {
  callId: string;
  rubricId: string;
  judgeModel: string;
  status: "complete" | "failed" | "skipped";
  overallScore: number | null;
  criteria: CriterionScore[];
  error: string | null;
  createdAt: string | null;
}

export interface ScoredCall {
  call: CallRecord;
  evaluation: CallEvaluation;
}

function qs(params: Record<string, string | number | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === "") continue;
    search.set(key, String(value));
  }
  const out = search.toString();
  return out ? `?${out}` : "";
}

export const getRubric = (rubricId?: string) =>
  apiRequest<RubricDefinition>(`/studio/evals/rubric${qs({ rubricId })}`);

export const getEvalSummary = (
  guideId: string,
  params: { rubricId?: string; from?: string; to?: string } = {},
) => apiRequest<EvalSummary>(`/studio/evals/summary${qs({ guideId, ...params })}`);

export const getEvalTimeseries = (
  guideId: string,
  params: { rubricId?: string; version?: number; from?: string; to?: string } = {},
) =>
  apiRequest<{
    rubricId: string;
    items: { date: string; count: number; overallAvg: number | null }[];
  }>(`/studio/evals/timeseries${qs({ guideId, ...params })}`);

export const listScoredCalls = (
  params: {
    guideId?: string;
    rubricId?: string;
    version?: number;
    from?: string;
    to?: string;
    limit?: number;
  } = {},
) => apiRequest<{ items: ScoredCall[] }>(`/studio/evals/calls${qs({ ...params })}`);

export const getCallEvaluation = (callId: string) =>
  apiRequest<CallEvaluation>(`/studio/evals/calls/${encodeURIComponent(callId)}`);

export const backfillEvals = (
  params: { guideId?: string; from?: string; to?: string; limit?: number; rescore?: boolean } = {},
) =>
  apiRequest<{
    queued: number;
    callIds: string[];
    dispatch: string;
    deduplicated: number;
    failed: number;
    dailyBudgetRemaining: number;
  }>(
    `/studio/evals/backfill${qs({ ...params, rescore: params.rescore ? "true" : undefined })}`,
    { method: "POST" },
  );

export const runCallEvaluation = (callId: string) =>
  apiRequest<{ status: string }>(`/studio/evals/calls/${encodeURIComponent(callId)}/run`, {
    method: "POST",
  });
